import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';

import { catchError, debounceTime, distinctUntilChanged, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { Config } from 'src/app/core/configs/config';

import { AuthService } from 'src/app/core/services/auth.service';
import { GoogleAnalyticService } from 'src/app/core/services/google-analytic.service';

import moment from 'moment';
import { BreakpointService } from '../../../../../core/services/breakpoint.service';
import { NgTemplateOutlet } from '@angular/common';
import { ESvgIcons } from '../../../../../core/enums/svg-icons';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatRipple } from '@angular/material/core';
import { ITripDestination } from '../../../../../core/types/interfaces/trip-elements.interface';

interface IOption {
  country_id: number;
  isAttraction: boolean;
  isMajorAirport: boolean;
  isRegion: boolean;
  label: string;
  val: number;
}

@Component({
  selector: 'app-add-destination-dialog',
  templateUrl: './add-destination-dialog.component.html',
  styleUrls: ['./add-destination-dialog.component.scss'],
  imports: [
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatCheckbox,
    NgTemplateOutlet,
    MatIcon,
    TranslateModule,
    MatRipple,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDestinationDialogComponent {
  public maintainDuration: boolean = false;
  public searchForm: FormControl<string> = new FormControl<string>('');
  public filteredOptions: IOption[] = [];
  public daysInDestination = 1;
  public readonly days: Array<{ value: number; text: string }> = [
    { value: 1, text: '1 day' },
    { value: 2, text: '2 days' },
    { value: 3, text: '3 days' },
    { value: 4, text: '4 days' },
    { value: 5, text: '5 days' },
  ];
  public selectedOption: any | null = null;
  protected readonly eSvgIcons = ESvgIcons;

  constructor(
    public dialogRef: MatDialogRef<AddDestinationDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      isSpecifyDates: boolean;
      currentTripEndDate: Date;
      isOptimizedMode: boolean;
    },
    private readonly cdr: ChangeDetectorRef,
    private readonly httpClient: HttpClient,
    private readonly authService: AuthService,
    private readonly googleAnalyticService: GoogleAnalyticService,
    protected breakpointService: BreakpointService,
  ) {
    this.initSearchFieldListener();
  }

  get newTripEndDate(): string {
    if (!this.data) return '';
    if (!this.data.isSpecifyDates) return '';
    return moment(this.data.currentTripEndDate)
      .add(this.daysInDestination, 'days')
      .format('DD MMM YYYY');
  }

  closeModal(body: {
    res: boolean;
    maintain: boolean;
    destination: any;
  }): void {
    this.dialogRef.close(body);
  }

  resetDestinationHandler(inputRef: HTMLInputElement) {
    inputRef.value = '';
    this.selectedOption = null;
    this.filteredOptions = [];
    inputRef.focus();
  }

  selectedDestinationHandler(ev: IOption): void {
    this.selectedOption = {
      countryId: ev.country_id,
      placeId: ev.val,
      name: ev.label,
      days: 1,
    };
  }

  toggleDaysDurationChanges(value: number): void {
    this.daysInDestination += value;
  }

  submitBtnHandler(): void {
    this.googleAnalyticService.setItineraryInteractionAnalytic({
      itinerary_interactions_type: 'add_destanation',
      userID: this.authService.userId,
    });
    this.selectedOption.days = this.daysInDestination;
    this.dialogRef.close({
      res: true,
      maintain: this.maintainDuration,
      destination: this.selectedOption,
    });
  }

  private initSearchFieldListener(): void {
    this.searchForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value) =>
          value
            ? this.httpClient
                .get<
                  IOption[]
                >(`${Config.apiUrl}actions/autocomplete.php?cc=1&term=${value}`)
                .pipe(
                  catchError(() => {
                    console.error('Error fetching autocomplete data');
                    return of([]);
                  }),
                )
            : of([]),
        ),
      )
      .subscribe((options) => {
        this.filteredOptions = options;
        this.cdr.markForCheck();
      });
  }
}
